import { Capacitor } from '@capacitor/core';
import { cloneElement, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { LuCircleAlert } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { Card, Mascot, Scene, SectionHeader } from '../../design-system/components';
import { cx } from '../../design-system/components/cx';
import { useIsTabletUp } from '../../hooks/useMediaQuery';
import { openExternalUrl } from '../../utils/open-external';
import { getShareBaseUrl } from '../../utils/share-url';
import { Field, fieldDescId } from '../a11y';

/*
 * The auth shell (redesign PR9, 2026-10-20): Login, Register and Forgot
 * password on the design-system foundations, one family. Styles live in
 * design-system/pages/auth.css (every class `.ee-auth-*`).
 *
 * From 768 the page splits: a warm art panel on the left with the brand and
 * the landing valley Scene with the waving giraffe, the form on the right.
 * The Scene is the 440x420 `mobile` art: the panel is near square at 1280
 * and taller than wide at 768, so the 1200x420 hero art lost most of its
 * width to the crop. It is MOUNTED only there (useIsTabletUp), so a phone never
 * downloads it; a phone shows the brand above the heading and a small waving
 * giraffe whose lower edge tucks behind the card (the card paints over it).
 *
 * The safe-area classes stay on the same two layers as before the redesign:
 * `safe-area-horizontal` on the root and `safe-area-vertical` on the form
 * column, so the iOS notch and home-indicator padding is unchanged.
 */

export interface AuthShellProps {
  /**
   * The heading without its last word. ReactNode so a heading can carry a
   * `lang` span (the public language page). Omitted while a page has nothing
   * to name yet (its loading and not-found states): no heading renders then.
   */
  title?: ReactNode;
  /** The heading's last word, in the text orange. The heading text is unchanged. */
  accent?: string;
  tagline?: ReactNode;
  /** Above the heading (the Check Your Email medallion). */
  lead?: ReactNode;
  /** The card's content: the banner, the form and the alternatives. */
  children: ReactNode;
  /** Small print under the card. */
  footer?: ReactNode;
}

function Brand() {
  return (
    <div className="ee-auth-brand">
      <img src="/logo.webp" alt="Logo" className="ee-auth-brand__logo" width={42} height={42} />
      <span className="ee-auth-brand__name">EmpathEase</span>
    </div>
  );
}

export function AuthShell({ title, accent, tagline, lead, children, footer }: AuthShellProps) {
  const hasArt = useIsTabletUp();
  return (
    <div className="ee-page ee-auth-page safe-area-horizontal">
      {hasArt ? (
        <div className="ee-auth-art">
          <Brand />
          <Scene
            section="landing"
            variant="mobile"
            mascot="wave"
            mascotPosition="center"
            fade={false}
            className="ee-auth-art__scene"
          />
        </div>
      ) : null}
      <div className="ee-auth-main safe-area-vertical">
        <div className="ee-auth-col">
          {hasArt ? null : <Brand />}
          {lead ? <div className="ee-auth-lead">{lead}</div> : null}
          {title != null ? (
            <SectionHeader
              title={title}
              accent={accent}
              tagline={tagline}
              as="h1"
              size="page"
              className={cx('ee-auth-head', !hasArt && 'ee-auth-head--sprite')}
            />
          ) : null}
          <div className="ee-auth-cardwrap">
            {hasArt ? null : (
              <span className="ee-auth-sprite" aria-hidden="true">
                <Mascot name="wave" loading="eager" />
              </span>
            )}
            <Card padding="lg" className="ee-auth-card">
              {children}
            </Card>
          </div>
          {footer ? <div className="ee-auth-footer">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** A server-side refusal, at the top of the card. Announced when it appears. */
export function AuthAlert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="ee-auth-alert">
      <LuCircleAlert className="ee-auth-alert__icon" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/** A field's own error: the icon and the message (Field renders the line). */
function AuthErrorText({ children }: { children: ReactNode }) {
  return (
    <>
      <LuCircleAlert className="ee-auth-field__error-icon" aria-hidden="true" />
      <span>{children}</span>
    </>
  );
}

export interface AuthFieldProps {
  /** The input's id; the label points at it. */
  id: string;
  label: string;
  /** Leading Lucide icon (decorative). */
  icon: IconType;
  /** The validation message, under the field. Replaces the helper while shown. */
  error?: string;
  /** Helper line under the field. */
  hint?: ReactNode;
  /** A control at the end of the input (the password visibility toggle). */
  trailing?: ReactNode;
  /** The <input>, with its register() props, type, id and placeholder. */
  children: ReactElement<{
    className?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
  }>;
}

/**
 * Field (components/a11y) with the shell's look: a leading icon and a
 * trailing slot around the input. Field owns the label, the helper and error
 * line (aria-live while it is an error) and its id (`fieldDescId`); because
 * the input sits in a wrapper, Field does not clone it, so this points the
 * input's aria-describedby at that same id. The input keeps its own
 * className and aria-describedby and gets aria-invalid while it has an error.
 */
export function AuthField({
  id,
  label,
  icon: Icon,
  error,
  hint,
  trailing,
  children,
}: AuthFieldProps) {
  const descId = hint || error ? fieldDescId(id) : undefined;
  const input = cloneElement(children, {
    className: cx(
      'ee-auth-input',
      !!trailing && 'ee-auth-input--trailing',
      children.props.className,
    ),
    'aria-invalid': error ? true : undefined,
    'aria-describedby': cx(children.props['aria-describedby'], descId) || undefined,
  });
  return (
    <Field
      label={label}
      variant="plain"
      controlId={id}
      className={cx('ee-auth-field', !!error && 'ee-auth-field--invalid')}
      labelClassName="ee-auth-field__label"
      hintClassName={error ? 'ee-auth-field__error' : 'ee-auth-field__hint'}
      hint={hint}
      error={error ? <AuthErrorText>{error}</AuthErrorText> : undefined}
    >
      <div className="ee-auth-field__control">
        <Icon className="ee-auth-field__icon" aria-hidden="true" />
        {input}
        {trailing}
      </div>
    </Field>
  );
}

export interface AuthCheckProps {
  /** The checkbox's text (may hold links: a link inside a label does not toggle it). */
  label: ReactNode;
  /** Helper line under the label. */
  hint?: ReactNode;
  /** The validation message. Replaces the helper while shown. */
  error?: string;
  /** The checkbox <input>, with its register() props and id. */
  children: ReactElement<{ id?: string; className?: string; 'aria-invalid'?: boolean }>;
}

/**
 * A checkbox on Field: Field labels it and wires the helper and error line
 * (same ids and aria-live as AuthField); this adds the shell's classes and
 * aria-invalid. auth.css draws the box before its label.
 */
export function AuthCheck({ label, hint, error, children }: AuthCheckProps) {
  const box = cloneElement(children, {
    className: cx('ee-auth-check__box', children.props.className),
    'aria-invalid': error ? true : undefined,
  });
  return (
    <Field
      label={label}
      variant="plain"
      className={cx('ee-auth-check', !!error && 'ee-auth-check--invalid')}
      labelClassName="ee-auth-check__label"
      hintClassName={error ? 'ee-auth-field__error' : 'ee-auth-check__hint'}
      hint={hint}
      error={error ? <AuthErrorText>{error}</AuthErrorText> : undefined}
    >
      {box}
    </Field>
  );
}

/**
 * Terms of Service or Privacy Policy, on its canonical route. It opens in a
 * new tab so a half-filled form survives. A native app has no tabs (iOS hands
 * a new-window request to the system, which cannot open the app's own
 * capacitor:// URL), so there it opens the web page in the in-app browser.
 */
export function AuthLegalLink({
  to,
  children,
}: {
  to: '/terms-of-use' | '/privacy';
  children: ReactNode;
}) {
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!Capacitor.isNativePlatform()) return;
    event.preventDefault();
    void openExternalUrl(`${getShareBaseUrl()}${to}`);
  };
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="ee-auth-terms__link"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

/** The "or" rule between the form and the other ways in. */
export function AuthDivider() {
  return (
    <div className="ee-auth-or">
      <span>or</span>
    </div>
  );
}

/** Google's four-colour G, as Google's sign-in branding asks. Decorative. */
export function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
